import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Package, Truck, BarChart3, Shield, Globe, ChevronRight, Building2, Clock } from 'lucide-react';

export default function LOMGroupPage() {
  const solutions = [
    {
      name: "JamInventra",
      tagline: "Smart Inventory & POS System",
      description: "Cloud-based inventory management and point-of-sale system designed for Jamaican small businesses. Track stock, manage sales, and grow your business.",
      status: "Live",
      link: "/jaminventra",
      icon: Package,
      features: ["Real-time stock tracking", "Barcode scanning", "Profit & Loss reports", "Multi-location support"],
      color: "#009B3A"
    },
    {
      name: "LOM Procure",
      tagline: "Procurement & Supply Chain",
      description: "Streamline your purchasing process. Manage suppliers, track orders, and optimize your supply chain — all from one platform.",
      status: "Coming Soon",
      link: null,
      icon: Truck,
      features: ["Supplier management", "Purchase order tracking", "Cost optimization", "Delivery scheduling"],
      color: "#007AFF"
    },
    {
      name: "LOM Analytics",
      tagline: "Business Intelligence Dashboard",
      description: "Turn your business data into actionable insights. Advanced reporting, forecasting, and performance tracking across all LOM services.",
      status: "Coming Soon",
      link: null,
      icon: BarChart3,
      features: ["Sales forecasting", "Performance dashboards", "Custom reports", "Market insights"],
      color: "#FFD700"
    },
    {
      name: "LOM TimeTrack",
      tagline: "Employee Time & Attendance",
      description: "Track employee clock-ins, manage schedules, and monitor attendance across your team. Built for businesses that need reliable workforce management.",
      status: "Coming Soon",
      link: null,
      icon: Clock,
      features: ["Clock-in & clock-out tracking", "Attendance reports", "Schedule management", "Team oversight dashboard"],
      color: "#FF6B35"
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="lom-group-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="lom-logo">
            <img src="/lom-group-logo.png" alt="LOM Solutions Jamaica Limited" className="h-14 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-sm font-medium text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors">About</a>
            <a href="#solutions" className="text-sm font-medium text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors">Solutions</a>
            <a href="#contact" className="text-sm font-medium text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors">Contact</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#0A0A0A] hover:bg-gray-50" data-testid="lom-login-btn">Log In</Button>
            </Link>
            <Link to="/jaminventra">
              <Button className="bg-[#0A0A0A] hover:bg-[#1C1C1E] text-white" data-testid="lom-launch-btn">
                Launch JamInventra <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#1C1C1E] to-[#2C2C2E]" />
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/5466271/pexels-photo-5466271.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-12 pb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
              <div className="w-2 h-2 rounded-full bg-[#009B3A] animate-pulse" />
              <span className="text-white/80 text-sm font-medium">Powering Jamaican Businesses</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight mb-4 leading-[0.95]">
              Building the<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#009B3A] via-[#FFD700] to-[#009B3A]">
                Future of Business
              </span><br />
              in Jamaica
            </h1>
            <p className="text-sm text-white/40 mb-4 tracking-widest uppercase font-medium">Solutions Today. Growth Tomorrow.</p>
            <p className="text-lg text-white/60 mb-10 max-w-xl leading-relaxed">
              LOM Solutions Jamaica Limited is a Jamaican logistics operations management company creating digital solutions that empower small and medium businesses to compete, grow, and thrive in the modern economy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#solutions">
                <Button size="lg" className="bg-white text-[#0A0A0A] hover:bg-white/90 h-13 px-8 text-base font-semibold" data-testid="hero-explore-btn">
                  Explore Our Solutions <ChevronRight className="ml-1 w-5 h-5" />
                </Button>
              </a>
              <a href="#contact">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-13 px-8 text-base" data-testid="hero-contact-btn">
                  Contact Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* L.O.M. Pillars Section */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 items-start">
            {/* Logistics */}
            <div className="flex flex-col items-center text-center" data-testid="pillar-logistics">
              <div className="w-20 h-20 mb-4 flex items-center justify-center">
                <svg viewBox="0 0 80 80" className="w-full h-full">
                  <rect x="8" y="52" width="18" height="3" rx="1" fill="#009B3A" opacity="0.6"/>
                  <rect x="8" y="58" width="14" height="3" rx="1" fill="#009B3A" opacity="0.5"/>
                  <rect x="8" y="64" width="10" height="3" rx="1" fill="#009B3A" opacity="0.4"/>
                  <rect x="8" y="70" width="6" height="3" rx="1" fill="#009B3A" opacity="0.3"/>
                  <path d="M30 70 L30 25 L50 10 L50 35 L65 20 L65 45 L50 35 L50 55 L30 70Z" fill="#009B3A"/>
                  <path d="M50 55 L50 35 L65 20 L65 45 L50 55Z" fill="#006B28"/>
                </svg>
              </div>
              <h3 className="text-lg font-black text-[#009B3A] tracking-wider uppercase">Logistics</h3>
            </div>

            {/* Divider */}
            <div className="hidden md:flex flex-col items-center text-center relative" data-testid="pillar-operations">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-px h-16 bg-gray-200" />
              <div className="w-20 h-20 mb-4 flex items-center justify-center">
                <svg viewBox="0 0 80 80" className="w-full h-full">
                  <circle cx="35" cy="45" r="18" fill="none" stroke="#D4A800" strokeWidth="6"/>
                  <circle cx="35" cy="45" r="8" fill="#D4A800"/>
                  <rect x="28" y="26" width="5" height="8" rx="2" fill="#D4A800" transform="rotate(-15 30 30)"/>
                  <rect x="48" y="38" width="5" height="8" rx="2" fill="#D4A800" transform="rotate(30 50 42)"/>
                  <rect x="38" y="58" width="5" height="8" rx="2" fill="#D4A800" transform="rotate(60 40 62)"/>
                  <rect x="18" y="55" width="5" height="8" rx="2" fill="#D4A800" transform="rotate(120 20 59)"/>
                  <rect x="15" y="35" width="5" height="8" rx="2" fill="#D4A800" transform="rotate(-60 17 39)"/>
                  <rect x="32" y="22" width="5" height="6" rx="2" fill="#D4A800"/>
                  <circle cx="55" cy="30" r="12" fill="none" stroke="#C49800" strokeWidth="5"/>
                  <circle cx="55" cy="30" r="5" fill="#C49800"/>
                  <rect x="49" y="16" width="4" height="6" rx="1.5" fill="#C49800"/>
                  <rect x="63" y="24" width="4" height="6" rx="1.5" fill="#C49800" transform="rotate(45 65 27)"/>
                  <rect x="60" y="38" width="4" height="6" rx="1.5" fill="#C49800" transform="rotate(90 62 41)"/>
                </svg>
              </div>
              <h3 className="text-lg font-black text-[#D4A800] tracking-wider uppercase">Operations</h3>
            </div>

            {/* Operations - mobile only (no divider) */}
            <div className="flex md:hidden flex-col items-center text-center" data-testid="pillar-operations-mobile">
              <div className="w-20 h-20 mb-4 flex items-center justify-center">
                <svg viewBox="0 0 80 80" className="w-full h-full">
                  <circle cx="35" cy="45" r="18" fill="none" stroke="#D4A800" strokeWidth="6"/>
                  <circle cx="35" cy="45" r="8" fill="#D4A800"/>
                  <circle cx="55" cy="30" r="12" fill="none" stroke="#C49800" strokeWidth="5"/>
                  <circle cx="55" cy="30" r="5" fill="#C49800"/>
                </svg>
              </div>
              <h3 className="text-lg font-black text-[#D4A800] tracking-wider uppercase">Operations</h3>
            </div>

            {/* Management */}
            <div className="flex flex-col items-center text-center relative" data-testid="pillar-management">
              <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 w-px h-16 bg-gray-200" />
              <div className="w-20 h-20 mb-4 flex items-center justify-center">
                <svg viewBox="0 0 80 80" className="w-full h-full">
                  <rect x="12" y="55" width="10" height="20" fill="#1a1a1a"/>
                  <rect x="26" y="45" width="10" height="30" fill="#1a1a1a"/>
                  <rect x="40" y="32" width="10" height="43" fill="#1a1a1a"/>
                  <rect x="54" y="18" width="10" height="57" fill="#1a1a1a"/>
                  <path d="M15 52 L32 38 L48 28 L62 12" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round"/>
                  <path d="M55 10 L65 10 L65 20" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="text-lg font-black text-[#1a1a1a] tracking-wider uppercase">Management</h3>
            </div>

            {/* Global Impact */}
            <div className="flex flex-col items-center text-center relative" data-testid="pillar-global">
              <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 w-px h-16 bg-gray-200" />
              <div className="w-20 h-20 mb-4 flex items-center justify-center">
                <svg viewBox="0 0 80 80" className="w-full h-full">
                  <ellipse cx="40" cy="40" rx="28" ry="28" fill="#009B3A"/>
                  <ellipse cx="38" cy="38" rx="14" ry="26" fill="#D4A800" transform="rotate(-15 38 38)"/>
                  <path d="M20 30 Q30 25 40 30 Q50 35 55 28" stroke="#009B3A" strokeWidth="2" fill="none"/>
                  <path d="M18 45 Q30 40 42 45 Q52 48 58 42" stroke="#009B3A" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              <h3 className="text-lg font-black text-[#009B3A] tracking-wider uppercase leading-tight">Global Impact</h3>
              <p className="text-sm font-bold text-[#D4A800] tracking-wider uppercase mt-0.5">Jamaican Roots</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold tracking-widest text-[#009B3A] uppercase mb-4">About LOM Solutions</p>
              <h2 className="text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-6">
                Jamaican Innovation.<br />Global Standards.
              </h2>
              <p className="text-lg text-[#0A0A0A]/60 mb-6 leading-relaxed">
                Founded in Jamaica, LOM Solutions Jamaica Limited builds logistics, operations, and management solutions that address the real challenges faced by Caribbean businesses. We understand the local market because we are part of it.
              </p>
              <p className="text-lg text-[#0A0A0A]/60 mb-8 leading-relaxed">
                Our mission is to bridge the technology gap for small and medium enterprises through affordable, accessible, and locally-designed digital tools. From inventory management to procurement and beyond — we're building the infrastructure that Jamaican businesses need to grow.
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-white rounded-lg border border-gray-100">
                  <div className="text-3xl font-black text-[#009B3A]">1</div>
                  <div className="text-sm text-[#0A0A0A]/60 mt-1">Live Product</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-gray-100">
                  <div className="text-3xl font-black text-[#007AFF]">3+</div>
                  <div className="text-sm text-[#0A0A0A]/60 mt-1">In Development</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-gray-100">
                  <div className="text-3xl font-black text-[#FFD700]">JA</div>
                  <div className="text-sm text-[#0A0A0A]/60 mt-1">Based in Jamaica</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/8729925/pexels-photo-8729925.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Business professional using technology"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0A0A0A] text-white p-6 rounded-xl max-w-xs">
                <p className="text-sm font-medium leading-relaxed">"Empowering Jamaican businesses with world-class technology solutions."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-widest text-[#007AFF] uppercase mb-4">Our Solutions</p>
            <h2 className="text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight">
              Digital Tools for Growth
            </h2>
            <p className="text-lg text-[#0A0A0A]/50 mt-4 max-w-2xl mx-auto">
              Purpose-built solutions designed for the Caribbean market. Each product addresses a specific business need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className={`relative p-8 rounded-2xl border-2 transition-all duration-300 hover:shadow-xl ${
                  solution.status === 'Live' 
                    ? 'border-[#009B3A]/30 bg-[#009B3A]/[0.02] hover:border-[#009B3A]' 
                    : 'border-gray-100 bg-gray-50/50 hover:border-gray-200'
                }`}
                data-testid={`solution-card-${solution.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {solution.status === 'Live' && (
                  <div className="absolute -top-3 right-6 bg-[#009B3A] text-white text-xs font-bold px-3 py-1 rounded-full">
                    LIVE
                  </div>
                )}
                {solution.status === 'Coming Soon' && (
                  <div className="absolute -top-3 right-6 bg-gray-400 text-white text-xs font-bold px-3 py-1 rounded-full">
                    COMING SOON
                  </div>
                )}
                
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${solution.color}15` }}>
                  <solution.icon className="w-7 h-7" style={{ color: solution.color }} />
                </div>
                
                <h3 className="text-2xl font-black text-[#0A0A0A] mb-1">{solution.name}</h3>
                <p className="text-sm font-medium mb-4" style={{ color: solution.color }}>{solution.tagline}</p>
                <p className="text-[#0A0A0A]/60 mb-6 leading-relaxed">{solution.description}</p>
                
                <ul className="space-y-2 mb-8">
                  {solution.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#0A0A0A]/70">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: solution.color }} />
                      {feat}
                    </li>
                  ))}
                </ul>

                {solution.link ? (
                  <Link to={solution.link}>
                    <Button className="w-full bg-[#009B3A] hover:bg-[#008030] text-white font-semibold" data-testid="launch-jaminventra-btn">
                      Launch JamInventra <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                ) : (
                  <Button disabled className="w-full bg-gray-200 text-gray-500 cursor-not-allowed">
                    Coming Soon
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white tracking-tight">Why LOM Solutions</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Globe, title: "Jamaican Built", desc: "Designed for the Caribbean market with local expertise" },
              { icon: Shield, title: "Secure & Reliable", desc: "Enterprise-grade security and 99.9% uptime" },
              { icon: Package, title: "Affordable", desc: "Pricing that makes sense for Jamaican businesses" },
              { icon: BarChart3, title: "Data-Driven", desc: "Turn your business data into growth opportunities" }
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#009B3A]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm font-semibold tracking-widest text-[#007AFF] uppercase mb-4">Get in Touch</p>
          <h2 className="text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-6">
            Let's Build Together
          </h2>
          <p className="text-lg text-[#0A0A0A]/50 mb-10 max-w-2xl mx-auto">
            Ready to digitize your business? Have questions about our solutions? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Corporate Footer */}
      <footer className="bg-[#0A0A0A]" data-testid="lom-footer">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">

            {/* Company Info */}
            <div className="lg:col-span-2">
              <img src="/lom-group-logo.png" alt="LOM Solutions Jamaica Limited" className="h-20 w-auto bg-white rounded-xl p-2.5 mb-5 shadow-lg" />
              <p className="text-white/50 text-sm leading-relaxed mb-5 max-w-sm">
                LOM Solutions Jamaica Limited is a logistics operations management company building digital solutions that empower Caribbean businesses to compete, grow, and thrive.
              </p>
              <p className="text-[#FFD700] text-sm font-medium italic mb-6">Solutions Today. Growth Tomorrow.</p>
              {/* Social / Contact Icons Row */}
              <div className="flex items-center gap-4">
                <a href="https://wa.me/18764507828" target="_blank" rel="noopener noreferrer" 
                   className="w-10 h-10 rounded-lg bg-white/10 hover:bg-[#25D366] flex items-center justify-center transition-colors group" data-testid="footer-whatsapp-icon">
                  <svg className="w-5 h-5 text-white/60 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
                <a href="tel:+18764507828" 
                   className="w-10 h-10 rounded-lg bg-white/10 hover:bg-[#007AFF] flex items-center justify-center transition-colors group" data-testid="footer-phone-icon">
                  <svg className="w-5 h-5 text-white/60 group-hover:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                </a>
                <a href="https://lomgroupja.com" target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group" data-testid="footer-web-icon">
                  <Globe className="w-5 h-5 text-white/60 group-hover:text-white" />
                </a>
                <a href="mailto:info@lomgroupja.com"
                   className="w-10 h-10 rounded-lg bg-white/10 hover:bg-[#009B3A] flex items-center justify-center transition-colors group" data-testid="footer-email-icon">
                  <svg className="w-5 h-5 text-white/60 group-hover:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                </a>
              </div>
            </div>

            {/* Our Solutions */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Our Solutions</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/jaminventra" className="text-white/50 hover:text-[#009B3A] text-sm transition-colors flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#009B3A]" />
                    JamInventra
                  </Link>
                </li>
                <li>
                  <span className="text-white/25 text-sm flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    LOM Procure <span className="text-[10px] ml-1 bg-white/10 px-1.5 py-0.5 rounded">Soon</span>
                  </span>
                </li>
                <li>
                  <span className="text-white/25 text-sm flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    LOM Analytics <span className="text-[10px] ml-1 bg-white/10 px-1.5 py-0.5 rounded">Soon</span>
                  </span>
                </li>
                <li>
                  <span className="text-white/25 text-sm flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                    LOM TimeTrack <span className="text-[10px] ml-1 bg-white/10 px-1.5 py-0.5 rounded">Soon</span>
                  </span>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Company</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-white/50 hover:text-white text-sm transition-colors">About Us</a></li>
                <li><a href="#solutions" className="text-white/50 hover:text-white text-sm transition-colors">Solutions</a></li>
                <li><a href="#contact" className="text-white/50 hover:text-white text-sm transition-colors">Contact</a></li>
                <li><Link to="/login" className="text-white/50 hover:text-white text-sm transition-colors">Client Login</Link></li>
                <li><Link to="/register" className="text-white/50 hover:text-white text-sm transition-colors">Get Started</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-[#25D366] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <div>
                    <a href="https://wa.me/18764507828" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm transition-colors">876-450-7828</a>
                    <p className="text-white/25 text-xs">WhatsApp & Call</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-[#25D366] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <div>
                    <a href="https://wa.me/18763773479" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm transition-colors">876-377-3479</a>
                    <p className="text-white/25 text-xs">WhatsApp & Call</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-[#007AFF] mt-0.5 flex-shrink-0" />
                  <div>
                    <a href="https://lomgroupja.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm transition-colors">lomgroupja.com</a>
                    <p className="text-white/25 text-xs">Official Website</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-[#009B3A] mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                  <div>
                    <a href="mailto:info@lomgroupja.com" className="text-white/50 hover:text-white text-sm transition-colors">info@lomgroupja.com</a>
                    <p className="text-white/25 text-xs">General Enquiries</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-[#FFD700] mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                  <div>
                    <p className="text-white/50 text-sm">Portmore, Jamaica</p>
                    <p className="text-white/25 text-xs">Caribbean</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="border-t border-white/10 pt-10 mb-10">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6 text-center">Frequently Asked Questions</h4>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div className="bg-white/5 rounded-lg p-5">
                <h5 className="text-white font-semibold text-sm mb-2">What does LOM stand for?</h5>
                <p className="text-white/40 text-sm leading-relaxed">LOM stands for Logistics, Operations, and Management — the three pillars that drive our solutions for Jamaican businesses.</p>
              </div>
              <div className="bg-white/5 rounded-lg p-5">
                <h5 className="text-white font-semibold text-sm mb-2">What is JamInventra?</h5>
                <p className="text-white/40 text-sm leading-relaxed">JamInventra is our flagship inventory management and POS system, built specifically for Jamaican small businesses. It's cloud-based and works on any device.</p>
              </div>
              <div className="bg-white/5 rounded-lg p-5">
                <h5 className="text-white font-semibold text-sm mb-2">How much does it cost?</h5>
                <p className="text-white/40 text-sm leading-relaxed">JamInventra plans start at J$2,499/month (Starter). We also offer Growth (J$4,999) and Pro (J$9,999) plans. All include a free 14-day trial. First month FREE!</p>
              </div>
              <div className="bg-white/5 rounded-lg p-5">
                <h5 className="text-white font-semibold text-sm mb-2">Do I need technical skills?</h5>
                <p className="text-white/40 text-sm leading-relaxed">Not at all. JamInventra is designed for simplicity. If you can use a phone, you can manage your inventory. We also provide training and WhatsApp support.</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-sm">
              &copy; 2026 LOM Solutions Jamaica Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-white/20 text-xs">JamInventra is a product of LOM Solutions Jamaica Limited</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
